<template>
    <Popper
        :id="id"
        :show="show"
        v-bind="listeners"
        :interactive="interactive"
        :offset-distance="offsetDistance"
        :placement="placement"
        class="basic-menu no-select !border-none !m-0"
        :class="computedClasses"
        @open:popper="onToggle(true)"
        @close:popper="onToggle(false)"
    >
        <slot name="activator"></slot>
        <template #content="{ close }">
            <div @touchmove="handleTouchMove(close)">
                <div
                    v-if="maxSm && isDefaultMenu"
                    class="flex justify-center p-[10px]"
                >
                    <div
                        class="w-[90px] h-[5px] rounded-[100px] bg-background-4"
                    ></div>
                </div>
                <slot name="content">
                    <BasicMenuList
                        v-if="items"
                        :class="maxHeightClass"
                        :items="items"
                        @click:item="emits('click:item', $event)"
                    >
                        <template
                            v-for="(_, slotName) in filteredSlots"
                            #[slotName]="slotData"
                        >
                            <slot
                                :name="slotName"
                                v-bind="slotData || {}"
                            />
                        </template>
                    </BasicMenuList>
                </slot>
            </div>
        </template>
    </Popper>
</template>

<script lang="ts" setup>
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
// @ts-ignore
import Popper from 'vue3-popper/dist/popper.esm';

import BasicMenuList, {
    type BasicMenuListEmits,
} from '@molecules/BasicMenu/BasicMenuList.vue';
import { useListeners } from '@composables/useListeners';
import type { BasicMenuListItemProps } from '@molecules/BasicMenu/BasicMenuListItem.vue';

export type BasicMenuEmits = BasicMenuListEmits & {
    (e: 'open:popper'): void;
    (e: 'close:popper'): void;
    (e: 'close:manual'): void;
};

const emits = defineEmits<BasicMenuEmits>();
const breakpoints = useBreakpoints(breakpointsTailwind);
const maxSm = breakpoints.smallerOrEqual('sm');

export type BasicMenuProps = {
    items?: BasicMenuListItemProps[];
    interactive?: boolean;
    isDefaultMenu?: boolean;
    offsetDistance?: string;
    id?: string;
    show?: boolean;
    maxHeightClass?: string;
    placement?: string;
};

const props = withDefaults(defineProps<BasicMenuProps>(), {
    interactive: true,
    items: undefined,
    id: '',
    offsetDistance: '0',
    isDefaultMenu: true,
    show: undefined,
    maxHeightClass: '',
    placement: 'bottom',
});

const handleTouchMove = (close: () => void) => {
    props.show === undefined && close();
};

onMounted(() =>
    window.addEventListener('touchmove', () => emits('close:manual'))
);

onUnmounted(() =>
    window.removeEventListener('touchmove', () => emits('close:manual'))
);

const slots = useSlots();

const filteredSlots = computed(() => {
    const filteredSlots: Record<string, any> = {};
    Object.keys(slots)
        .filter((slotName) => !['activator'].includes(slotName))
        .forEach((slotName) => {
            filteredSlots[slotName] = slots[slotName];
        });
    return filteredSlots;
});

const { listeners } = useListeners();

const isOpenLocally = ref(props.show);

const onToggle = (value: boolean) => {
    isOpenLocally.value = value;
    value ? emits('open:popper') : emits('close:popper');
};

watch(
    () => props.show,
    (newValue) => {
        if (isOpenLocally.value !== newValue) {
            isOpenLocally.value = newValue;
        }
    }
);

const computedClasses = computed(() => {
    return [
        isOpenLocally.value ? 'open' : 'close',
        { 'default-menu': props.isDefaultMenu },
    ];
});
</script>

<style>
:root {
    --popper-theme-background-color: rgb(var(--color-background-5));
    --popper-theme-background-color-hover: rgb(var(--color-background-5));
    --popper-theme-text-color: rgb(var(--color-font-4));
    --popper-theme-border-width: 1px;
    --popper-theme-border-style: solid rgb(var(--color-input-dark));
    --popper-theme-border-radius: 10px;
    --popper-theme-padding: 0;
    --popper-theme-box-shadow: none;
}
</style>

<style scoped>
.default-menu.close :deep(.popper) {
    @apply max-sm:!translate-y-[0%];
}

.default-menu.open :deep(.popper) {
    @apply max-sm:!translate-y-[-100%];
}

.default-menu :deep(.popper) {
    @apply w-screen sm:w-[300px] max-sm:!left-0 max-sm:!pb-[30px] max-sm:!opacity-100 max-sm:!transition-transform
    max-sm:!rounded-b-none max-sm:!top-[100%] max-sm:!fixed max-sm:!inset-auto max-sm:!duration-150 max-sm:!ease-linear overflow-auto;
}

.default-menu :deep(.popper.fade-enter-active, .popper.fade-leave-active) {
    @apply max-sm:!transition-transform max-sm:!duration-150 max-sm:!ease-linear;
}

.default-menu :deep(.popper.fade-enter-from, .popper.fade-leave-to) {
    @apply max-sm:!translate-y-[0%];
}

.default-menu :deep(.popper.fade-enter-to, .popper.fade-leave-from) {
    @apply max-sm:!translate-y-[-100%];
}

.default-menu :deep(.basic-menu-list-item) {
    @apply max-sm:!py-[13px];
}

.default-menu :deep(.basic-menu-list-item-text) {
    @apply max-sm:!text-[16px] max-sm:!leading-[24px] max-sm:!tracking-[-0.32px];
}
</style>
